<template>
  <with-loader :id="id" :loading="isLoading" use-as-overlay @wheel.native.prevent>
    <div class="flex flex-col justify-between py-3">
      <div class="flex items-center justify-center">
        <h3 class="text-center text-base text-gray-dark font-bold flex justify-center">
          {{ title }}

          <vs-tooltip class="flex ml-1 items-center" position="bottom">
            <template #content>
              <div v-html="titleHint"/>
            </template>

            <vs-icon icon="info" class="text-gray cursor-default"/>
          </vs-tooltip>
        </h3>

        <chart-toolbar class="absolute right-0 top-0"
                       :min="axisXRange.from"
                       :max="axisXRange.to"
                       :value-from="axisXValues.valueFrom"
                       :value-to="axisXValues.valueTo"
                       :min-range="minRange"
                       :is-loading="isLoading"
                       @zoom="handleZoom"
                       @set-settings="setZoomSettings"
                       @show-drawer="$emit('show-drawer')"
        />
      </div>

      <vue-apex-charts ref="areaChart"
                       :height="`${chartHeight}px`"
                       :series="chartData"
                       :options="options"
                       class="md:pt-6"
                       :class="`cursor-${chartCursor}`"
                       @wheel.native.prevent="handleChartScroll"
                       @mousedown.native="handleChartMouseDown"
                       @mouseup.native="handleChartMouseUp"
                       @mouseenter.native="setChartCursor($event.composedPath()[0])"
      />

      <slot name="tooltip"
            :show-tooltip="showTooltip"
            :tooltip-props="tooltipProps"
            :axis-x-values="axisXValues"
      />

      <range-slider class="pl-16 pr-2 my-10"
                    ref="slider"
                    :min-from="axisXRange.from"
                    :max-to="axisXRange.to"
                    :value-from="chartCursor === 'grabbing' ?  axisXValuesCache.valueFrom : axisXValues.valueFrom"
                    :value-to="chartCursor === 'grabbing' ? axisXValuesCache.valueTo : axisXValues.valueTo"
                    :min-range="minRange"
                    :formatter="formatDatetime"
                    @change-range="setAxisXValue"
      />
      <div
        class="pt-7 flex flex-wrap flex-1 gap-x-7 gap-y-2 border-0 border-t border-solid border-gray-a10"
      >
        <chart-legend v-for="({ legendType }, i) in data" :key="i" :type="legendType"/>
      </div>

    </div>
  </with-loader>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import tailwind from '../../../../tailwind.config'
import ChartLegend from '@/components/analytics/charts/ChartLegend'
import ChartToolbar from '@/components/analytics/charts/ChartToolbar'
import RangeSlider from '@/components/base-components/RangeSlider'
import isEmpty from 'ramda/src/isEmpty'
import max from 'ramda/src/max'
import min from 'ramda/src/min'
import datetimeUtils from '@/utils/datetime.utils'
import equals from 'ramda/src/equals'
import WithLoader from '@/components/common/WithLoader'
import isNil from 'ramda/src/isNil'
import reverse from 'ramda/src/reverse'
import { PERIODS } from '../../../utils/filter.constants'

export default {
  name: 'AreaChart',

  components: {
    VueApexCharts,
    ChartLegend,
    ChartToolbar,
    RangeSlider,
    WithLoader
  },

  props: {
    data: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    titleHint: {
      type: String,
      default: ''
    },
    axisNames: {
      type: Object,
      default: () => {}
    },
    id: {
      type: String,
      default: ''
    },
    axisXRange: {
      type: Object,
      default: () => { return { from: 0, to: 100 }}
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    minRange: {
      type: Number,
      default: 1
    }
  },

  data() {
    return {
      axisXValues: { valueFrom: null, valueTo: null },
      axisXValuesCache: { valueFrom: null, valueTo: null },
      tooltipProps: {
        series: [],
        categoryIndex: 0,
        position: { top: 0, left: 0, movedRight: false },
        timeStamp: 0
      },
      windowWidth: window.innerWidth,
      showTooltip: false,
      isZoomedBySelection: false,
      chartCursor: 'crosshair',
      zoomStep: 0.05,
      PERIODS,
      lastMouseXPosition: 0,
      lastDatapointXPosition: 0
    }
  },

  computed: {
    hasData() {
      return !isEmpty(this.data)
    },

    chartHeight() {
      if (this.windowWidth < 768) return this.windowWidth * 0.5

      return min(this.windowWidth * 0.25, 250)
    },

    chartData() {
      return reverse(this.data)
    },

    options() {
      return {
        chart: {
          id: this.id,
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 400,
            animateGradually: {
              enabled: true,
              delay: 100
            },
            dynamicAnimation: {
              enabled: true,
              speed: 200
            }
          },
          type: 'area',
          stacked: true,
          toolbar: {
            show: false
          },
          events: {
            mouseMove: (e, chart, config) => this.renderTooltip(e, chart, config),
            mouseLeave: (e, chart) => this.handleChartBlur(e, chart),
            zoomed: (chart, config) => this.setAxisXValue(config.xaxis.min, config.xaxis.max),
            scrolled: (chart, config) => this.panChart(config.xaxis.min, config.xaxis.max),
            beforeZoom: (chartContext, { xaxis }) => this.handleZoomBySelection(xaxis)
          }
        },
        colors: this.chartData.map(chartData => tailwind.theme.colors[chartData.colorType]),
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth'
        },
        fill: {
          type: 'gradient',
          colors: this.chartData.map(chartData => tailwind.theme.colors[chartData.colorType]),
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.3,
            opacityTo: 0.6,
            stops: [0, 80]
          }
        },
        legend: {
          show: false
        },
        yaxis: {
          title: {
            text: this.axisNames.x,
            offsetX: -6,
            style: {
              color: tailwind.theme.colors['gray-dark'],
              fontSize: tailwind.theme.fontSize.base,
              fontFamily: 'Figtree, Montserrat, Helvetica, Arial, sans-serif',
              fontWeight: tailwind.theme.fontWeight.semibold
            }
          }
        },
        xaxis: {
          type: 'datetime',
          tickPlacement: 'on',
          axisTicks: {
            show: false
          },
          axisBorder: {
            show: false
          },
          labels: {
            datetimeUTC: false,
            datetimeFormatter: {
              year: 'yyyy',
              month: 'MMM',
              day: 'dd MMM',
              hour: 'HH:mm'
            }
          },
          tooltip: {
            enabled: false
          }
        },
        tooltip: {
          // to keep highlighting of cursor position(value) in the chart
          custom: () => ''
        }
      }
    }
  },

  watch: {
    axisXValues: {
      immediate: false,
      deep: true,
      handler(newAxisXValues, oldAxisXValues) {
        if (isNil(oldAxisXValues) || isNil(oldAxisXValues.valueFrom) || isNil(oldAxisXValues.valueTo)) return
        if (equals(newAxisXValues, oldAxisXValues)) return

        const { valueFrom, valueTo } = newAxisXValues
        if (!this.isZoomedBySelection) this.zoom(valueFrom, valueTo)
        this.emitZoomChange(valueFrom, valueTo)
      }
    },

    isLoading: {
      immediate: false,
      async handler(isLoading) {
        if (isLoading) return

        // time for mapping data and getting them into chart
        await this.$nextTick()
        this.zoom(this.axisXValues.valueFrom, this.axisXValues.valueTo)
      }
    }
  },

  async mounted() {
    this.setZoomSettings('selection')
    window.addEventListener('resize', this.setWindowWidth)

    // zoom and axis range needs to be set up after data are processed by apex chart
    await this.$nextTick()
    this.setAxisXValue(this.axisXRange.from, this.axisXRange.to)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setWindowWidth)
  },

  methods: {
    // tooltip methods
    renderTooltip(e, chart, config) {
      if (this.isZoomedBySelection || this.chartCursor === 'grabbing') return

      const movedRight = e.pageX > this.lastMouseXPosition
      const position = this.getTooltipPosition(movedRight)
      this.lastMouseXPosition = e.pageX
      if (!position) return

      this.setChartCursor(e.composedPath()[0])
      if (!this.showTooltip) this.showTooltip = true

      this.tooltipProps = {
        currentTimestamp: config.dataPointIndex === -1 ? this.tooltipProps.currentTimestamp : chart.w.globals.seriesX[0]?.[config.dataPointIndex],
        series: chart.w.globals.series,
        categoryIndex: config.dataPointIndex === -1 ? this.tooltipProps.categoryIndex : config.dataPointIndex,
        position: { top: position.top, left: position.left, movedRight }
      }
    },

    getTooltipPosition(movedRight) {
      const dataPointEl = this.$refs.areaChart.$el.querySelector('.apexcharts-xcrosshairs')
      // prevent tooltip rendering when there is no data point under cursor
      if (+dataPointEl.attributes.x.value === 0) return

      const bodyRect = document.body.getBoundingClientRect()
      const datapointRect = dataPointEl.getBoundingClientRect()

      // prevent tooltip rendering when new x position would be same
      if (this.lastDatapointXPosition === datapointRect.left) return

      const top = datapointRect.top - bodyRect.top - 50 // offset to push tooltip above x-axis
      const left = datapointRect.left + (movedRight ? -10 : 10) // offset left, right to allow moving through data-points
      this.lastDatapointXPosition = datapointRect.left

      return { top, left }
    },

    handleChartBlur(e) {
      this.chartCursor = this.$refs.areaChart.chart.w.globals.panEnabled ? 'grab' : 'crosshair'
      this.setChartCursor(e.composedPath()[0])
      this.setAxisXValue(this.axisXValuesCache.valueFrom, this.axisXValuesCache.valueTo)
      if (e.toElement?.classList.contains('popup-el')) return

      this.showTooltip = false
    },

    // zoom methods
    setZoomSettings(mode) {
      const canvas = this.$refs.areaChart.$el.querySelector('svg')
      if (mode === 'pan') {
        this.$refs.areaChart.chart.w.globals.panEnabled = true
        this.$refs.areaChart.chart.w.globals.zoomEnabled = false
        this.chartCursor = 'grab'
        this.setChartCursor(canvas)
      } else {
        this.$refs.areaChart.chart.w.globals.panEnabled = false
        this.$refs.areaChart.chart.w.globals.zoomEnabled = true
        this.chartCursor = 'crosshair'
        this.setChartCursor(canvas)
      }
    },

    handleZoom(type) {
      if (['zoomIn', 'zoomOut'].includes(type)) {
        const { from, to } = this.getZoomRange(type)
        this.setAxisXValue(from, to)
      } else {
        this.setAxisXValue(this.axisXRange.from, this.axisXRange.to)
      }
    },

    handleZoomBySelection(xaxis) {
      if (xaxis.max - xaxis.min >= this.minRange) return

      return {
        xaxis: {
          min: this.axisXValues.valueFrom,
          max: this.axisXValues.valueFrom + this.minRange
        }
      }
    },

    getZoomRange(type) {
      const { valueFrom, valueTo } = this.axisXValues
      const { from: minFrom, to: maxTo } = this.axisXRange
      const range = valueTo - valueFrom
      const zoomDelta = range * this.zoomStep

      if (type === 'zoomIn') {
        const from = range <= this.minRange ? valueFrom - this.minRange : valueFrom + zoomDelta
        const to = range <= this.minRange ? valueTo + this.minRange : valueTo - zoomDelta
        return { from, to }
      } else {
        const from = valueFrom - zoomDelta
        const to = valueTo + zoomDelta
        return { from: max(from, minFrom), to: min(to, maxTo) }
      }
    },

    zoom(from, to) {
      this.$refs.areaChart.zoomX(from, to)
    },

    panChart(valueFrom, valueTo) {
      const { from, to } = this.getPanRange(valueFrom, valueTo)
      this.setAxisXValue(from, to)
    },

    getPanRange(valueFrom, valueTo) {
      const { from, to } = this.axisXRange
      const range = this.axisXValues.valueTo - this.axisXValues.valueFrom
      if (valueTo >= to) {
        this.zoom(to - range, to)
        return { from: to - range, to: to }
      }

      if (valueFrom <= from) {
        this.zoom(from, from + range)
        return { from: from, to: from + range }
      }

      return { from: valueFrom, to: valueTo }
    },

    formatDatetime(timestamp) {
      return datetimeUtils.toMoment(timestamp).format('LLL')
    },

    // handlers
    handleChartMouseDown(e) {
      this.isZoomedBySelection = true
      this.showTooltip = false
      if (this.chartCursor === 'grab') {
        this.chartCursor = 'grabbing'
        this.setAxisXValue(this.axisXValuesCache.valueFrom, this.axisXValuesCache.valueTo)
      }

      this.setChartCursor(e.composedPath()[0])
    },

    handleChartMouseUp(e) {
      this.isZoomedBySelection = false
      if (this.chartCursor === 'grabbing') {
        this.chartCursor = 'grab'
        this.setAxisXValue(this.axisXValuesCache.valueFrom, this.axisXValuesCache.valueTo)
      }

      this.setChartCursor(e.composedPath()[0])
    },

    handleChartScroll(e) {
      this.showTooltip = false
      const zoomDirection = e.deltaY > 0 ? 'zoomOut' : 'zoomIn'
      this.handleZoom(zoomDirection)
    },

    // setters
    setChartCursor(canvas) {
      canvas.style.cursor = this.chartCursor
    },

    setAxisXValue(valueFrom, valueTo) {
      this.axisXValuesCache = { valueFrom, valueTo }
      if (this.chartCursor !== 'grabbing') {
        this.axisXValues = { valueFrom, valueTo }
      }
    },

    emitZoomChange(valueFrom, valueTo) {
      this.$emit('zoom', { from: valueFrom, to: valueTo })
    },

    setWindowWidth() {
      this.windowWidth = window.innerWidth
    }
  }
}
</script>
